import WhiteLabelLogo from "@/components/_module/loading/white-label-logo";
import { buttonVariants } from "@/components/ui/button/button";
import ErrorPage from "@/components/ui/ErrorPage";
import PageLoader from "@/components/ui/PageLoader";
import PendingScreen from "@/components/ui/PendingScreen";
import SuccessScreen from "@/components/ui/SuccessScreen";
import Typography from "@/components/ui/typography";
import { useAuth } from "@/context/auth-context";
import useGetIdentityVerificationSessions from "@/features/identity-verification/useGetIdentityVerificationSessions";
import useGetUserInformation from "@/features/auth/useGetUserInformation";
import {
	CLEAR_APPLICATION_ERROR_MESSAGE,
	clearPendingClearSession,
} from "@/features/identity-verification/pendingClearSession";
import { useEffect } from "react";
import { Link, useLoaderData, useNavigate } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";

function ValidateVerificationUI() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { location_id } = useLoaderData({ from: "__root__" });
	const sessionQuery = useGetIdentityVerificationSessions({
		guest_id: user.id,
		user_id: user.id,
		location_id: location_id,
	});
	useGetUserInformation({
		enabled: sessionQuery.isSuccess,
	});

	// CLEAR redirected back: this page shows the session's outcome.
	useEffect(() => {
		if (sessionQuery.isSuccess) clearPendingClearSession();
	}, [sessionQuery.isSuccess]);

	return (
		<PageLoader
			query={sessionQuery}
			customLoadingUI={
				<div className="h-[100dvh] bg-primary-600 flex items-center justify-center flex-col gap-4">
					<WhiteLabelLogo />
					<Typography className="text-white" variant={"h4"}>
						Verifying your identity...
					</Typography>
				</div>
			}
		>
			{(data) => {
				if (
					data.status === "awaiting_manual_review" ||
					data.status === "processing_data"
				) {
					return (
						<PendingScreen
							title="Your identity is being verified"
							description="This may take some time. Please wait while we review your information."
							onClose={() => {
								navigate({
									to: "/main/home",
								});
							}}
						>
							<Link
								className={buttonVariants({
									variant: "default",
								})}
								to={"/main/home"}
							>
								Back to dashboard
							</Link>
						</PendingScreen>
					);
				}

				if (data.status === "success" || data.status === "manual_success") {
					return (
						<SuccessScreen
							title={
								<>
									ID Verified <br />
									successfully!
								</>
							}
							onClose={() => {
								navigate({
									to: "/main/home",
								});
							}}
							description={
								<>
									You're ready to start adding credit <br /> cards and
									converting credit to cash.
								</>
							}
						>
							<Link
								to={"/main/card/new"}
								className={buttonVariants({
									variant: "default",
									className: "w-full max-w-64",
								})}
							>
								Add a credit card <PlusIcon />
							</Link>
						</SuccessScreen>
					);
				}

				if (data.status === "awaiting_user_input") {
					return (
						<ErrorPage
							message={CLEAR_APPLICATION_ERROR_MESSAGE}
							onClose={() => {
								navigate({ to: "/main/home" });
							}}
						/>
					);
				}

				return (
					<ErrorPage
						message="We couldn't verifiy your identity"
						onClose={() => {
							navigate({ to: "/main/home" });
						}}
					/>
				);
			}}
		</PageLoader>
	);
}

export default ValidateVerificationUI;
